import { observable, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

export class AdminApiTokenStore extends LocalModel {
    @observable overview = { data: [], total: 0 };
    @observable detail = null;
    @observable usage = null;
    @observable alerts = { data: [], total: 0 };
    @observable status = null;
    @observable loading = false;
    @observable saving = false;
    @observable error = '';
    @observable blockForm = { reason: '' };
    requestRevision = 0;
    pendingAction = null;

    constructor() {
        super('adminApiToken', {
            namePlural: 'adminApiTokens',
            api: {},
        });
    }

    @action
    clearAll() {
        this.requestRevision += 1;
        this.overview = { data: [], total: 0 };
        this.detail = null;
        this.usage = null;
        this.alerts = { data: [], total: 0 };
        this.status = null;
        this.loading = false;
        this.saving = false;
        this.error = '';
        this.blockForm = { reason: '' };
        this.pendingAction = null;
    }

    async request(path, input) {
        const response = await util.fetchApi(`/api/apitokens/admin${path}`, {
            method: input ? 'POST' : 'GET',
        }, input);
        if (response.status !== 200) {
            const error = new Error(response.message || 'Unable to load API key usage');
            error.status = response.status;
            throw error;
        }
        return response.data;
    }

    async loadView(id, filters = {}) {
        const revision = ++this.requestRevision;
        this.updateKeyValue('loading', true);
        this.updateKeyValue('error', '');
        this.updateKeyValue('detail', null);
        this.updateKeyValue('usage', null);
        const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== '')).toString();
        try {
            const [status, result, usage, alerts] = await Promise.all([
                this.request('/status'),
                this.request(id ? `/${id}` : `/?${query}`),
                id ? this.request(`/${id}/usage?days=${filters.days || 7}`) : null,
                id ? this.request(`/${id}/alerts?limit=20&offset=${filters.alertOffset || 0}`) : null,
            ]);
            if (revision !== this.requestRevision) return;
            this.updateKeyValue('status', status);
            this.updateKeyValue(id ? 'detail' : 'overview', result);
            this.updateKeyValue('usage', usage);
            this.updateKeyValue('alerts', alerts || { data: [], total: 0 });
        } catch (err) {
            if (revision !== this.requestRevision) return;
            if (err.status === 401 || err.status === 403) this.clearAll();
            this.updateKeyValue('error', err.message);
        } finally {
            if (revision === this.requestRevision) this.updateKeyValue('loading', false);
        }
    }

    async changeBlock(blocked, filters) {
        if (this.saving || !this.detail || !this.blockForm.reason.trim()) return false;
        this.updateKeyValue('saving', true);
        this.updateKeyValue('error', '');
        const viewRevision = this.requestRevision;
        const token = this.detail.token;
        const reason = this.blockForm.reason.trim();
        const fingerprint = JSON.stringify([token.id, token.revision, reason, blocked]);
        try {
            if (this.pendingAction?.fingerprint !== fingerprint) {
                const bytes = new Uint8Array(16);
                window.crypto.getRandomValues(bytes);
                this.pendingAction = {
                    fingerprint, actionId: Array.from(bytes, n => n.toString(16).padStart(2, '0')).join(''),
                };
            }
            await this.request(`/${token.id}/${blocked ? 'block' : 'unblock'}`, {
                reason, revision: token.revision, actionId: this.pendingAction.actionId,
            });
            this.pendingAction = null;
            this.updateKeyValue('blockForm', { reason: '' });
            if (viewRevision === this.requestRevision) await this.loadView(token.id, filters);
            return true;
        } catch (err) {
            if (err.status === 409) {
                this.pendingAction = null;
                await this.loadView(token.id, filters);
            }
            if (err.status === 401 || err.status === 403) this.clearAll();
            this.updateKeyValue('error', err.message);
            return false;
        } finally {
            this.updateKeyValue('saving', false);
        }
    }
}

export default new AdminApiTokenStore();
